// ReportDialog.js
import { useState } from 'react';
import styled from 'styled-components';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ModalShell, Icon } from '../ui/shell';
import { Btn } from '../ui/primitives';
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';

const ActionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rMd};
  background: ${({ theme }) => theme.surface2};

  @media (max-width: 480px) {
    flex-wrap: wrap;
  }
`;

const ActionIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex: none;
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.accentSoft};
  color: ${({ theme }) => theme.accentStrong};
`;

const ActionCopy = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActionTitle = styled.div`
  font-weight: 650;
  font-size: 14px;
  color: ${({ theme }) => theme.text};
`;

const ActionSub = styled.p`
  margin: 3px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.4;
`;

const Hint = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.textMuted};
`;

// -------- Utility: Group Questions by Case Study --------
const groupQuestions = (questions = []) => {
  const grouped = {};
  const standalone = [];

  questions.forEach((q) => {
    if (q.caseStudyId) {
      if (!grouped[q.caseStudyId]) {
        grouped[q.caseStudyId] = { passage: q.passage, items: [] };
      }
      grouped[q.caseStudyId].items.push(q);
    } else {
      standalone.push(q);
    }
  });

  return { grouped, standalone };
};

const ReportDialog = ({ open, onClose, reportData }) => {
  const [loading, setLoading] = useState(false);
  const [loading1, setLoading1] = useState(false);
  if (!open || !reportData) return null;

  const assessmentName = reportData.testName || 'Assessment';

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  // -------- Admin Report --------
  const handleDownload = async () => {
    const {
    testName,
    type,
    vendor,
    examCode,
    examTime,
    totalMarks,
    questions = [],
  } = reportData || {};

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

   // Total page placeholder string
  const totalPagesExp = "{total_pages_count_string}";

  // Add header/footer
 const addHeaderFooter = () => {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    // --- Header (top right with mixed colors, no gap) ---
    doc.setFontSize(12);

    // Print "Study" (purple)
    doc.setTextColor(128, 0, 128);
    const studyX = pageW - 120; // starting point
    const studyY = 30;
    doc.text("Study", studyX, studyY);

    // Measure width of "Study"
    const studyWidth = doc.getTextWidth("Study");

    // Print "AK.ai" (green) right after "Study"
    doc.setTextColor(0, 128, 0);
    doc.text("AK.ai", studyX + studyWidth + 2, studyY);

    // --- Make StudyAK.ai clickable ---
    const fullText = "StudyAK.ai";
    const fullWidth = doc.getTextWidth(fullText);
    doc.link(studyX, studyY - 10, fullWidth + 4, 14, {
      url: "https://studyak.ai/ptr-assessment",
    });

    // --- Footer (bottom center) ---
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(
      "Copyright © 2025 StudyAK, All rights reserved.",
      pageW / 2,
      pageH - 20,
      { align: "center" }
    );

    // --- Footer (bottom right, page numbers) ---
    doc.text(
      `Page ${i} of ${totalPagesExp}`,
      pageW - 80,
      pageH - 20
    );
  }
};
  // Title
  doc.setFontSize(18);
  doc.setTextColor(98, 0, 234);
  doc.text("Question Paper", 40, 40);

  // Test Details
  const testRows = [
    ["Name", testName || "—"],
    ["Type", type || "—"],
    ["Vendor", vendor || "—"],
    ["Exam Code", examCode || "—"],
    ["Exam Time", examTime ? `${examTime} mins` : "—"],
    ["Total Marks", totalMarks || "—"],
  ];

  autoTable(doc, {
    startY: 60,
    head: [["Field", "Value"]],
    body: testRows,
    theme: "grid",
    styles: { fontSize: 10, cellPadding: 6 },
    headStyles: { fillColor: [103, 58, 183], textColor: 255 },
  });

  let y = (doc.lastAutoTable?.finalY || 80) + 20;
  const { grouped, standalone } = groupQuestions(questions);

  // --- Helpers ---
  const checkPageBreak = (heightNeeded = 20) => {
    if (y + heightNeeded > pageH - 60) {
      doc.addPage();
      y = 60;
    }
  };

  // --- Render grouped case study questions ---
  for (const group of Object.values(grouped)) {
    if (group.passage) {
      doc.setFontSize(12);
      doc.setTextColor(80, 0, 150);

      const isUrl = /^https?:\/\/.+\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(
        group.passage
      );

      if (isUrl) {
  try {
    // Call Spring Boot API instead of hitting blob URL directly
    const apiUrl = process.env.REACT_APP_API_URL;
    setLoading(true)
    const response = await fetch(
      apiUrl+`/api/image/fetch?url=${group.passage}`,{
      headers: {
        'Content-Type': "application/json",
        "Accept": "*/*",
        "Access-Control-Allow-Origin": "*",
        'Authorization': getBasicAuthHeader(),
      },
    }
    );
    setLoading(false)
    if (!response.ok) throw new Error("Image API call failed");

    const blob = await response.blob();

    // Convert blob to Base64 for jsPDF
    const base64Img = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    if (base64Img) {
      const imgProps = doc.getImageProperties(base64Img);
      const imgWidth = pageW - 80;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      checkPageBreak(imgHeight + 20);
      const format = base64Img.startsWith("data:image/png") ? "PNG" : "JPEG";
      doc.addImage(base64Img, format, 40, y, imgWidth, imgHeight);
      y += imgHeight + 20;
    } else {
      throw new Error("Failed to convert image");
    }
  } catch (err) {
    console.error("Error loading image:", err);
    const wrapped = doc.splitTextToSize(
      `Passage: [Image could not be loaded: ${group.passage}]`,
      pageW - 80
    );
    checkPageBreak(wrapped.length * 14 + 20);
    doc.text(wrapped, 40, y);
    y += wrapped.length * 14 + 20;
  }
      } else {
        const wrapped = doc.splitTextToSize(
          `Passage: ${group.passage}`,
          pageW - 80
        );
        checkPageBreak(wrapped.length * 14 + 20);
        doc.text(wrapped, 40, y);
        y += wrapped.length * 14 + 20;
      }
    }

    // Questions under this passage
    for (const q of group.items) {
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);

      const wrappedQ = doc.splitTextToSize(
        `Q: ${q?.question || "—"}`,
        pageW - 80
      );
      checkPageBreak(wrappedQ.length * 14 + 20);
      doc.text(wrappedQ, 40, y);
      y += wrappedQ.length * 14 + 10;

q?.answer?.forEach((opt, idx) => {
  const isCorrect = opt?.isCorrect === 1 || opt?.is_correct === 1;

  // Wrap text
  const wrappedOpt = doc.splitTextToSize(
    `${String.fromCharCode(65 + idx)}. ${opt?.answer || "—"}`,
    pageW - 100
  );

  checkPageBreak(wrappedOpt.length * 14 + 10);

  if (isCorrect) {
    // Background rectangle for each line of wrapped text
    wrappedOpt.forEach((line, lineIdx) => {
      const lineWidth = doc.getTextWidth(line);
      const lineHeight = 14;

      // Draw rectangle behind the text (light green shade)
      doc.setFillColor(200, 255, 200); // light green shade
      doc.rect(58, y + lineIdx * lineHeight - 10, lineWidth + 6, lineHeight, "F");

      // Print text over the shade
      doc.setTextColor(0, 100, 0); // dark green text
      doc.text(line, 60, y + lineIdx * lineHeight);
    });
  } else {
    // Normal option (black text, no shade)
    doc.setTextColor(0, 0, 0);
    doc.text(wrappedOpt, 60, y);
  }

  y += wrappedOpt.length * 14 + 10;
});

// Reset color
doc.setTextColor(0, 0, 0);

      if (q?.hint) {
        doc.setTextColor(80, 80, 200);
        const wrappedHint = doc.splitTextToSize(`Hint: ${q.hint}`, pageW - 100);
        checkPageBreak(wrappedHint.length * 14 + 10);
        doc.text(wrappedHint, 60, y);
        y += wrappedHint.length * 14 + 10;
      }

      if (q?.explanation) {
        doc.setTextColor(100, 100, 100);
        const wrappedExp = doc.splitTextToSize(
          `Explanation: ${q.explanation}`,
          pageW - 100
        );
        checkPageBreak(wrappedExp.length * 14 + 10);
        doc.text(wrappedExp, 60, y);
        y += wrappedExp.length * 14 + 10;
      } else {
        y += 10;
      }
    }
  }

  // --- Render standalone questions ---
  for (const q of standalone) {
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);

    const wrappedQ = doc.splitTextToSize(
      `Q: ${q?.question || "—"}`,
      pageW - 80
    );
    checkPageBreak(wrappedQ.length * 14 + 20);
    doc.text(wrappedQ, 40, y);
    y += wrappedQ.length * 14 + 10;

   q?.answer?.forEach((opt, idx) => {
  const isCorrect = opt?.isCorrect === 1 || opt?.is_correct === 1;

  // Wrap text
  const wrappedOpt = doc.splitTextToSize(
    `${String.fromCharCode(65 + idx)}. ${opt?.answer || "—"}`,
    pageW - 100
  );

  checkPageBreak(wrappedOpt.length * 14 + 10);

  if (isCorrect) {
    // Background rectangle for each line of wrapped text
    wrappedOpt.forEach((line, lineIdx) => {
      const lineWidth = doc.getTextWidth(line);
      const lineHeight = 14;

      // Draw rectangle behind the text (light green shade)
      doc.setFillColor(200, 255, 200); // light green shade
      doc.rect(58, y + lineIdx * lineHeight - 10, lineWidth + 6, lineHeight, "F");

      // Print text over the shade
      doc.setTextColor(0, 100, 0); // dark green text
      doc.text(line, 60, y + lineIdx * lineHeight);
    });
  } else {
    // Normal option (black text, no shade)
    doc.setTextColor(0, 0, 0);
    doc.text(wrappedOpt, 60, y);
  }

  y += wrappedOpt.length * 14 + 10;
});

// Reset color
doc.setTextColor(0, 0, 0);


    if (q?.hint) {
      doc.setTextColor(80, 80, 200);
      const wrappedHint = doc.splitTextToSize(`Hint: ${q.hint}`, pageW - 100);
      checkPageBreak(wrappedHint.length * 14 + 10);
      doc.text(wrappedHint, 60, y);
      y += wrappedHint.length * 14 + 10;
    }

    if (q?.explanation) {
      doc.setTextColor(100, 100, 100);
      const wrappedExp = doc.splitTextToSize(
        `Explanation: ${q.explanation}`,
        pageW - 100
      );
      checkPageBreak(wrappedExp.length * 14 + 10);
      doc.text(wrappedExp, 60, y);
      y += wrappedExp.length * 14 + 10;
    } else {
      y += 10;
    }
  }

  addHeaderFooter();

  // Replace total page placeholder with actual number
  if (typeof doc.putTotalPages === "function") {
    doc.putTotalPages(totalPagesExp);
  }

  // Save file
  const safeName = (testName || "assessment").replace(/\s+/g, "_");
  doc.save(`${safeName}_assessment_paper.pdf`);
  };

// -------- Student Report --------
const handleDownloadStudent = async () => {
  const {
    testName,
    type,
    vendor,
    examCode,
    examTime,
    totalMarks,
    questions = [],
  } = reportData || {};

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Total page placeholder string
  const totalPagesExp = "{total_pages_count_string}";

  // Add header/footer
  const addHeaderFooter = () => {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    // --- Header (top right with mixed colors, no gap) ---
    doc.setFontSize(12);

    // Print "Study" (purple)
    doc.setTextColor(128, 0, 128);
    const studyX = pageW - 120; // starting point
    const studyY = 30;
    doc.text("Study", studyX, studyY);

    // Measure width of "Study"
    const studyWidth = doc.getTextWidth("Study");

    // Print "AK.ai" (green) right after "Study"
    doc.setTextColor(0, 128, 0);
    doc.text("AK.ai", studyX + studyWidth + 2, studyY);

    // --- Make StudyAK.ai clickable ---
    const fullText = "StudyAK.ai";
    const fullWidth = doc.getTextWidth(fullText);
    doc.link(studyX, studyY - 10, fullWidth + 4, 14, {
      url: "https://studyak.ai/ptr-assessment",
    });

    // --- Footer (bottom center) ---
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(
      "Copyright © 2025 StudyAK, All rights reserved.",
      pageW / 2,
      pageH - 20,
      { align: "center" }
    );

    // --- Footer (bottom right, page numbers) ---
    doc.text(
      `Page ${i} of ${totalPagesExp}`,
      pageW - 80,
      pageH - 20
    );
  }
};
  // Title
  doc.setFontSize(18);
  doc.setTextColor(98, 0, 234);
  doc.text("Student Question Paper", 40, 40);

  // Test Details
  const testRows = [
    ["Name", testName || "—"],
    ["Type", type || "—"],
    ["Vendor", vendor || "—"],
    ["Exam Code", examCode || "—"],
    ["Exam Time", examTime ? `${examTime} mins` : "—"],
    ["Total Marks", totalMarks || "—"],
  ];

  autoTable(doc, {
    startY: 60,
    head: [["Field", "Value"]],
    body: testRows,
    theme: "grid",
    styles: { fontSize: 10, cellPadding: 6 },
    headStyles: { fillColor: [103, 58, 183], textColor: 255 },
  });

  let y = (doc.lastAutoTable?.finalY || 80) + 20;
  const { grouped, standalone } = groupQuestions(questions);

  // --- Helpers ---
  const checkPageBreak = (heightNeeded = 20) => {
    if (y + heightNeeded > pageH - 60) {
      doc.addPage();
      y = 60;
    }
  };

  // --- Render grouped case study questions ---
  for (const group of Object.values(grouped)) {
    if (group.passage) {
      doc.setFontSize(12);
      doc.setTextColor(80, 0, 150);

      const isUrl = /^https?:\/\/.+\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(
        group.passage
      );

      if (isUrl) {
  try {
    // Call Spring Boot API instead of hitting blob URL directly
    const apiUrl = process.env.REACT_APP_API_URL;
    setLoading1(true)
    const response = await fetch(
      apiUrl+`/api/image/fetch?url=${group.passage}`,{
      headers: {
        'Content-Type': "application/json",
        "Accept": "*/*",
        "Access-Control-Allow-Origin": "*",
        'Authorization': getBasicAuthHeader(),
      },
    }
    );
    setLoading1(false)
    if (!response.ok) throw new Error("Image API call failed");

    const blob = await response.blob();

    // Convert blob to Base64 for jsPDF
    const base64Img = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    if (base64Img) {

      const imgProps = doc.getImageProperties(base64Img);
      const imgWidth = pageW - 80;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      checkPageBreak(imgHeight + 20);
      const format = base64Img.startsWith("data:image/png") ? "PNG" : "JPEG";
      doc.addImage(base64Img, format, 40, y, imgWidth, imgHeight);
      y += imgHeight + 20;
    } else {
      throw new Error("Failed to convert image");
    }
  } catch (err) {
    console.error("Error loading image:", err);
    const wrapped = doc.splitTextToSize(
      `Passage: [Image could not be loaded: ${group.passage}]`,
      pageW - 80
    );
    checkPageBreak(wrapped.length * 14 + 20);
    doc.text(wrapped, 40, y);
    y += wrapped.length * 14 + 20;
  }
      } else {
        const wrapped = doc.splitTextToSize(
          `Passage: ${group.passage}`,
          pageW - 80
        );
        checkPageBreak(wrapped.length * 14 + 20);
        doc.text(wrapped, 40, y);
        y += wrapped.length * 14 + 20;
      }
    }

    // Questions under this passage
    for (const q of group.items) {
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);

      const wrappedQ = doc.splitTextToSize(
        `Q: ${q?.question || "—"}`,
        pageW - 80
      );
      checkPageBreak(wrappedQ.length * 14 + 20);
      doc.text(wrappedQ, 40, y);
      y += wrappedQ.length * 14 + 10;

      q?.answer?.forEach((opt, idx) => {
  // Wrap only the text (no tick mark)
  const wrappedOpt = doc.splitTextToSize(
    `${String.fromCharCode(65 + idx)}. ${opt?.answer || "—"}`,
    pageW - 100
  );

  checkPageBreak(wrappedOpt.length * 14 + 10);
  // Print option text
  doc.text(wrappedOpt, 60, y);

  y += wrappedOpt.length * 14 + 10;
});

        y += 10;
    }
  }

  // --- Render standalone questions ---
  for (const q of standalone) {
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);

    const wrappedQ = doc.splitTextToSize(
      `Q: ${q?.question || "—"}`,
      pageW - 80
    );
    checkPageBreak(wrappedQ.length * 14 + 20);
    doc.text(wrappedQ, 40, y);
    y += wrappedQ.length * 14 + 10;

    q?.answer?.forEach((opt, idx) => {

  // Wrap only the text (no tick mark)
  const wrappedOpt = doc.splitTextToSize(
    `${String.fromCharCode(65 + idx)}. ${opt?.answer || "—"}`,
    pageW - 100
  );

  checkPageBreak(wrappedOpt.length * 14 + 10);

  // Print option text
  doc.text(wrappedOpt, 60, y);

  y += wrappedOpt.length * 14 + 10;
});
      y += 10;
  }

  addHeaderFooter();

  // Replace total page placeholder with actual number
  if (typeof doc.putTotalPages === "function") {
    doc.putTotalPages(totalPagesExp);
  }

  // Save file
  const safeName = (testName || "student_paper").replace(/\s+/g, "_");
  doc.save(`${safeName}_student_paper.pdf`);
};

  return (
    <ModalShell
      title="Instructor Resource"
      sub={assessmentName}
      onClose={onClose}
      max="520px"
      foot={(
        <Btn type="button" $variant="ghost" onClick={onClose}>
          Close
        </Btn>
      )}
    >
      <Hint>Download a PDF resource for this assessment.</Hint>
      <ActionList>
        <ActionRow>
          <ActionIcon aria-hidden="true">
            <Icon name="doc" size={18} />
          </ActionIcon>
          <ActionCopy>
            <ActionTitle>Solution and Answer Guide</ActionTitle>
            <ActionSub>Correct answers with explanations for instructors.</ActionSub>
          </ActionCopy>
          <Btn
            type="button"
            $variant="subtle"
            onClick={handleDownload}
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? (
              <ButtonSpinnerSlot size={16} />
            ) : (
              <>
                <Icon name="download" size={15} />
                Download
              </>
            )}
          </Btn>
        </ActionRow>
        <ActionRow>
          <ActionIcon aria-hidden="true">
            <Icon name="assess" size={18} />
          </ActionIcon>
          <ActionCopy>
            <ActionTitle>Question bank</ActionTitle>
            <ActionSub>Student-facing paper with questions only.</ActionSub>
          </ActionCopy>
          <Btn
            type="button"
            onClick={handleDownloadStudent}
            disabled={loading1}
            aria-busy={loading1}
          >
            {loading1 ? (
              <ButtonSpinnerSlot size={16} />
            ) : (
              <>
                <Icon name="download" size={15} />
                Download
              </>
            )}
          </Btn>
        </ActionRow>
      </ActionList>
    </ModalShell>
  );
};

export default ReportDialog;
